// 我的（my tab）业务域接口，写法见 README.md
import type { HouseItem } from './common'
import type { EstateItem } from './home'
import type { LoginResult } from './login'
import { http, httpGet, httpPost } from '@/http'

/**
 * 查询当前登录用户信息（替代 v1 /mini/queryPersonalInfo；返回与登录接口一模一样的 LoginVo，
 * v2 只消费其中的 userInfo（UserVo），城市取 userInfo.cityCode / cityName）
 */
export const getLoginUserInfoApi = () => httpGet<LoginResult>('/hxyj/mini/getLoginUserInfo')

/** 联卖认证状态（v1 接口 /mini/jointSale/getState 返回） */
export interface JointSaleState {
  /** 是否联卖人员（认证通过且启用） */
  isJointSale: boolean
  /** 状态：1-启用 / 2-关闭（未认证场景=认证被驳回） / 3-启动审批中（未认证场景=认证审核中） / 4-关闭审批中 */
  jointSaleState: number
  /** 状态描述 */
  jointSaleStateDesc: string
  /** 备注（驳回时为驳回原因，展示在认证卡 desc） */
  remark: string
}

/** 查询当前用户联卖认证状态 */
export const getJointSaleStateApi = () => httpGet<JointSaleState>('/hxyj/mini/jointSale/getState')

/** 联卖人员基础信息（v1 接口 /mini/jointSale/getBaseInfo 返回） */
export interface JointSaleBaseInfo {
  /** 姓名 */
  username: string
  /** 手机号 */
  telephone: string
  /** 联卖昵称（分享房源时客户看到的名称） */
  nickname: string
  /** 头像地址 */
  profileUrl: string
  /**
   * 认证记录状态：null-无记录（第一次注册） / 1-现为联卖经纪人 / 2-以前是现在不是。
   * 认证页据此分流提交接口：1 走修改（updateBaseInfo），null 走注册（register），2 带申诉理由直接走注册（申诉）
   */
  state: number | null
}

/** 获取联卖人员信息（认证页回填；首次认证无记录，静默失败由调用方兜底） */
export function getJointSaleBaseInfoApi() {
  return http<JointSaleBaseInfo | null>({ url: '/hxyj/mini/jointSale/getBaseInfo', method: 'GET', hideErrorToast: true })
}

/** 能否注册联卖的校验结果 */
export interface CheckCanRegisterResult {
  /** false 表示海星员工/加盟人员，不允许注册联卖 */
  canRegister: boolean
  /** 该手机号已入驻的 B 端部门（确认页展示） */
  department?: string
  /** 是否已在 B 端入驻（加盟） */
  isJointVenture?: boolean
}

/** 注册前置校验（拦截海星内部员工/加盟人员，v1 接口 /mini/jointSale/checkCanRegister） */
export const checkCanRegisterApi = () => httpGet<CheckCanRegisterResult>('/hxyj/mini/jointSale/checkCanRegister')

/** 注册联卖入参（确认页提交，department / isJointVenture 透传 checkCanRegister 的结果） */
export interface JointSaleRegisterParams {
  username: string
  telephone: string
  nickname?: string
  profileUrl?: string
  cityCode?: string
  department?: string
  isJointVenture?: boolean
  /** 申诉理由（state=2 以前是联卖经纪人现在不是、重新注册时必传；首次注册/修改基础信息不传） */
  remark?: string
}

/** 注册成为联卖人员（v1 接口 /mini/jointSale/register） */
export const registerJointSaleApi = (data: JointSaleRegisterParams) => httpPost<null>('/hxyj/mini/jointSale/register', data)

/** 修改联卖人员信息入参 */
export type JointSaleUpdateParams = Pick<JointSaleRegisterParams, 'username' | 'telephone' | 'nickname' | 'profileUrl'>

/** 修改联卖人员信息（仅现为联卖经纪人 state=1 时使用，v1 接口 /mini/jointSale/updateBaseInfo） */
export const updateJointSaleBaseInfoApi = (data: JointSaleUpdateParams) => httpPost<null>('/hxyj/mini/jointSale/updateBaseInfo', data)

/** 浏览记录房源类型：1-二手房 2-租房 */
export type BrowseRecordType = 1 | 2

/** 浏览记录响应：data 为「日期 → 当日浏览房源列表」映射（后端在业务 data 里又嵌了一层 data） */
export interface BrowseRecordResult {
  data?: Record<string, HouseItem[]>
}

/** 查询浏览记录（v1 接口 /mini/housing-browse-record/getHousingBrowseRecord） */
export function getHousingBrowseRecordApi(type: BrowseRecordType) {
  return httpGet<BrowseRecordResult>('/hxyj/mini/housing-browse-record/getHousingBrowseRecord', { type })
}

/** 查询关注房源列表（v1 接口 /mini/getFollowHousingList，无参数、一次性全量返回不分页） */
export const getFollowHousingListApi = () => httpPost<HouseItem[]>('/hxyj/mini/getFollowHousingList')

/**
 * 查询关注小区列表（/mini/houseEstateFollow/selectFollow，无参数、一次性全量返回不分页）。
 * 返回 HousingParticularCommunityVo，与小区列表 EstateItem 同构，直接复用其类型
 */
export const getFollowCommunityListApi = () => httpGet<EstateItem[]>('/hxyj/mini/houseEstateFollow/selectFollow')

/**
 * 面访评价保存入参（接口文档「面访保存请求」）。
 * 除 miniLatitude / miniLongitude / score 外，其余字段都来自经纪人面访二维码携带的参数，
 * 原样回传即可；扫普通二维码时取自链接 query（string），小程序码 scanInfo 场景可能是 number
 */
export interface FaceEvaluateParams {
  /** 经纪人 id */
  agentId?: string | number
  /** 经纪人姓名 */
  agentUserName?: string
  /** 部门 id */
  departmentId?: string | number
  /** 房源 id */
  houseId?: string | number
  /** 房源标题 */
  houseTitle?: string
  /** 经纪人端（APP 生成二维码时）纬度，后端据此校验双方实际距离 */
  appLatitude?: string | number
  /** 经纪人端经度 */
  appLongitude?: string | number
  /** 小程序端定位纬度 */
  miniLatitude: number
  /** 小程序端定位经度 */
  miniLongitude: number
  /** 评分 0-10（页面 5 星制 ×2 上送） */
  score: number
}

/** 面访评价保存结果（接口业务 data） */
export const FaceEvaluateSaveResult = {
  /** 保存成功 */
  Success: 1,
  /** 与经纪人距离过远，未通过校验 */
  OutOfRange: 2,
  /** 保存失败 */
  Fail: 3,
} as const

/** 面访评价保存结果码（接口业务 data） */
export type FaceEvaluateSaveResultCode = (typeof FaceEvaluateSaveResult)[keyof typeof FaceEvaluateSaveResult]

/** 保存面访服务评价（v1 接口 /mini/saveUnilateralInterviews，扫经纪人面访二维码进入的服务评价页提交） */
export const saveUnilateralInterviewsApi = (data: FaceEvaluateParams) => httpPost<FaceEvaluateSaveResultCode>('/hxyj/mini/saveUnilateralInterviews', data)
